/**
 * Printer Configuration Page (Settings → Printer Settings)
 * Per-section bill/KOT printer routing, plus takeaway routing for this device.
 */

import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { selectEffectiveOutlet } from '@store/outlets';
import { BusinessType } from '@shared/types';
import { Grid2X2, Printer } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import { usePrinterConfiguration } from '../../../hooks/printers/usePrinterConfiguration';
import {
  createTourElementResolver,
  getQueuedOnboardingTour,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import { selectAllSections } from '../../../store/sections/sectionSelectors';
import { fetchSections } from '../../../store/sections/sectionThunks';
import { OutletSection } from '../../../types/section';
import { SaveSectionPrinterConfigRequest } from '../../../types/printer';
import { Button, ReadOnlyAccessNotice } from '../../common';
import { SectionPrinterSetupModal } from './SectionPrinterSetupModal';
import { TakeAwayPrintersTab } from './TakeAwayPrintersTab';

export const PrinterConfigurationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const sections = useAppSelector(selectAllSections);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sectionForSetup, setSectionForSetup] = useState<OutletSection | null>(
    null,
  );
  const queuedTourStep = getQueuedOnboardingTour();
  const {
    outletId,
    canManage,
    isSaving,
    printerDeviceId,
    printerSelectOptions,
    departmentOptions,
    mappingsForSection,
    hasPrinterConfigForSection,
    getSectionBillPrinterId,
    getSectionDepartmentPrinterMap,
    takeAwayBillPrinterId,
    takeAwayDepartmentPrinterMap,
    saveSectionConfig,
    saveTakeAwayConfig,
  } = usePrinterConfiguration();

  const isRestaurantOutlet = selectedOutlet
    ? selectedOutlet.business_type === BusinessType.RESTAURANT_BAR
    : true;
  const hasPrinters = printerSelectOptions.length > 0;

  useEffect(() => {
    if (outletId && isRestaurantOutlet) {
      dispatch(fetchSections(outletId));
    }
  }, [dispatch, isRestaurantOutlet, outletId]);

  usePowerSyncChange(['outlet_sections'], () => {
    if (outletId && isRestaurantOutlet) {
      dispatch(fetchSections(outletId));
    }
  });

  // Sections & Tables links here with ?section=<id> to open that section's
  // setup directly.
  const requestedSectionId = Number(searchParams.get('section'));
  useEffect(() => {
    if (!requestedSectionId || !hasPrinters) return;

    const section = sections.find((s) => s.id === requestedSectionId);
    if (!section) return;

    setSectionForSetup(section);
    searchParams.delete('section');
    setSearchParams(searchParams, { replace: true });
  }, [
    hasPrinters,
    requestedSectionId,
    searchParams,
    sections,
    setSearchParams,
  ]);

  const printerNameById = useMemo(
    () =>
      new Map(
        printerSelectOptions.map((option) => [option.value, option.label]),
      ),
    [printerSelectOptions],
  );

  // Memoized so the setup modal doesn't reset the user's in-progress
  // selections on every re-render (it re-seeds whenever these props change).
  const setupBillPrinterId = useMemo(
    () =>
      sectionForSetup ? getSectionBillPrinterId(sectionForSetup.id) : null,
    [getSectionBillPrinterId, sectionForSetup],
  );
  const setupDepartmentPrinterMap = useMemo(
    () =>
      sectionForSetup ? getSectionDepartmentPrinterMap(sectionForSetup.id) : {},
    [getSectionDepartmentPrinterMap, sectionForSetup],
  );

  const handleSaveSectionSetup = useCallback(
    async (payload: SaveSectionPrinterConfigRequest) => {
      if (await saveSectionConfig(payload)) {
        setSectionForSetup(null);
      }
    },
    [saveSectionConfig],
  );

  const sectionPrintersTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.printerConfigurationPage,
        ),
        popover: {
          title: 'Printer configuration',
          description:
            'Choose which printer handles bills and each department KOT for every section. Add printers under Printer Settings → All Printers first.',
          side: 'bottom' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(
            ONBOARDING_TOUR_TARGETS.sectionConfigurePrintersButton,
          ),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.sectionsTable),
        ]),
        popover: {
          title: 'Map printers for each section',
          description:
            'Use Configure to assign the bill printer and department KOT printers for that section.',
          side: 'left' as const,
        },
      },
    ],
    [],
  );

  const takeawayPrintersTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.takeawayPage),
        popover: {
          title: 'Set takeaway printer routing',
          description:
            'Choose the printer that should handle each department KOT and the bill printer for takeaway orders on this device.',
          side: 'top' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.takeawaySaveButton,
        ),
        popover: {
          title: 'Save the takeaway configuration',
          description:
            'Once every department and the bill printer are selected, save the setup so takeaway orders print correctly.',
          side: 'left' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    isEnabled: queuedTourStep === 'section_printers',
    stepId: 'section_printers',
    steps: sectionPrintersTourSteps,
  });

  useOnboardingDriverTour({
    isEnabled: queuedTourStep === 'takeaway_printers',
    stepId: 'takeaway_printers',
    steps: takeawayPrintersTourSteps,
  });

  if (!outletId) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8 text-sm text-gray-600">
        Select a specific outlet to manage printer configurations.
      </div>
    );
  }

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.printerConfigurationPage}
    >
      <div>
        <h2 className="text-base font-semibold uppercase tracking-wide text-black">
          Printer Configuration
        </h2>
        <p className="text-xs text-gray-600">
          Route bills and department KOTs to the right printer for each section
          and for takeaway orders.
        </p>
      </div>

      {!canManage ? (
        <ReadOnlyAccessNotice message="You can view printer configuration here, but only authorized roles can change it." />
      ) : null}

      {!hasPrinters ? (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>
            This outlet has no printers yet. Add printers before configuring
            routing.
          </span>
          <Link to="/settings/printers/all">
            <Button className="text-xs">
              <Printer size={14} />
              Add printers
            </Button>
          </Link>
        </div>
      ) : null}

      {isRestaurantOutlet ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Grid2X2 size={16} className="text-gray-500" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
              Sections
            </h3>
          </div>

          {sections.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-600">
              No sections yet. Create sections under{' '}
              <Link
                to="/dashboard/sections-tables"
                className="font-medium text-pos-primary underline"
              >
                Sections &amp; Tables
              </Link>{' '}
              first.
            </div>
          ) : (
            <div
              className="overflow-hidden rounded-xl border border-gray-100 bg-white"
              data-onboarding-tour={ONBOARDING_TOUR_TARGETS.sectionsTable}
            >
              <div className="max-h-[32rem] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Bill Printer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        KOT Printers
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map((section, index) => {
                      const billPrinterId = getSectionBillPrinterId(section.id);
                      const kotCount = mappingsForSection(section.id).filter(
                        (mapping) =>
                          mapping.operation === 'KOT' &&
                          departmentOptions.some(
                            (d) => d.id === mapping.department_id,
                          ),
                      ).length;
                      const isConfigured = hasPrinterConfigForSection(
                        section.id,
                      );

                      return (
                        <tr
                          key={section.id}
                          className="border-b border-gray-100 transition-colors hover:bg-gray-50/50"
                        >
                          <td className="px-4 py-3 text-sm font-medium uppercase text-gray-900">
                            {section.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {billPrinterId
                              ? printerNameById.get(billPrinterId) || '—'
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {kotCount}/{departmentOptions.length} departments
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                              <span
                                className={`inline-block h-2 w-2 rounded-full ${
                                  isConfigured ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                              />
                              {isConfigured ? 'Configured' : 'Incomplete'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              disabled={!hasPrinters}
                              onClick={() => setSectionForSetup(section)}
                              data-onboarding-tour={
                                index === 0
                                  ? ONBOARDING_TOUR_TARGETS.sectionConfigurePrintersButton
                                  : undefined
                              }
                              className="text-sm font-medium text-pos-primary transition-colors hover:text-pos-secondary disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {canManage ? 'CONFIGURE' : 'VIEW'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      ) : null}

      <section>
        <TakeAwayPrintersTab
          canManage={canManage}
          outletId={outletId}
          deviceId={printerDeviceId}
          isSaving={isSaving}
          printerOptions={printerSelectOptions}
          departments={departmentOptions}
          billPrinterId={takeAwayBillPrinterId}
          departmentPrinterMap={takeAwayDepartmentPrinterMap}
          onSubmit={saveTakeAwayConfig}
        />
      </section>

      <SectionPrinterSetupModal
        isOpen={Boolean(sectionForSetup)}
        isSaving={isSaving}
        canManage={canManage}
        outletId={outletId}
        sectionId={sectionForSetup?.id || null}
        sectionName={sectionForSetup?.name || 'Section'}
        printerOptions={printerSelectOptions}
        departments={departmentOptions}
        billPrinterId={setupBillPrinterId}
        departmentPrinterMap={setupDepartmentPrinterMap}
        onClose={() => setSectionForSetup(null)}
        onSubmit={handleSaveSectionSetup}
      />
    </div>
  );
};
